/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React from "react";
import Graph from "./Graph";
import { useEffect, useState } from "react";
import fetchHelper from "../../services/fetchHelper";
import Filter from "../../components/Filter";
import { useContext } from "react";
import { LayoutContext } from "../../components/layout";
import useError from "../../hooks/useError";

const dData = require("./device-population.json");

const ISO = [
  { value: "PRELIMINARY", label: "PRELIMINARY" },
  { value: "TERMINATED", label: "REMOVED" },
  { value: "NOTASSIGNED", label: "NOTASSIGNED" },
  { value: "FINAL", label: "FINAL" },
];

const keys = ["Pacemaker", "CRT-D", "CRT-P", "ICD", "ILR"];

const AnalyticsDevice = () => {
  const dd = ["OTHER", "BOSTONSCI", "BIOTRONIK", "STJUDE", "MEDTRONIC"];
  const { user } = useContext(LayoutContext);
  const [lIndex, setlIndex] = useState(null);
  const [totalByKeys, setTotalByKeys] = useState({});
  const [data, setData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({});
  const showDefaultApiError = useError();

  useEffect(() => {
    if (user && user.locations && locations.length) {
      console.log("######", locations, user.locations);
      const index = locations.map((l) => l.value).indexOf(user.locations[0].id);
      if (index >= 0) {
        setlIndex(index);
      } else {
        setlIndex(0);
      }
    }
  }, [user, locations]);

  useEffect(() => {
    if (user) fetchLocation();
    setListData(dData);
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [filter]);

  useEffect(() => {
    const d = {};
    data.forEach((item) => {
      keys.forEach((key) => {
        d[key] = d[key] || 0;
        d[key] = d[key] + item[key];
      });
    });
    setTotalByKeys(d);
  }, [data]);

  const queryDateFormat = (date) => {
    let year = date.getFullYear();
    let month = (1 + date.getMonth()).toString().padStart(2, "0");
    let day = date.getDate().toString().padStart(2, "0");

    return year + "-" + month + "-" + day;
  };

  const setListData = (data) => {
    const list = data.map((item) => {
      let sortVal = dd.indexOf(item.mfr);
      if (sortVal < 0) sortVal = 999;
      return {
        ...item,
        Pacemaker: item.pacemaker,
        "CRT-D": item.crtd,
        "CRT-P": item.crtp,
        ICD: item.icd,
        ILR: item.ilr,
        total: item.pacemaker + item.crtd + item.crtp + item.icd + item.ilr,
        sortVal,
      };
    });
    list.sort((a, b) => a.sortVal - b.sortVal);
    setData(list);
  };

  const fetchData = async () => {
    const { endDate: toDate, startDate: fromDate, where } = filter;
    if (!where) return false;
    const urlParams = {
      where: where,
    };
    if (fromDate) urlParams.fromDate = queryDateFormat(fromDate);
    if (toDate) urlParams.toDate = queryDateFormat(toDate);

    const urlQuery = new URLSearchParams(urlParams);
    setLoading(true);
    let url = "/patientmanagement/rest/interrogations/activePatientsDeviceCannedReport?" + urlQuery;
    // if (process.env.NODE_ENV === "development") url = "/jsons/device-population.json?" + urlQuery;

    try {
      const data = await fetchHelper.get({ url });
      setListData(data);
    } catch (err) {
      showDefaultApiError(err);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocation = async () => {
    try {
      let url = "/patientmanagement/rest/location/serviceReport";
      //   if (process.env.NODE_ENV === "development") url = "/serviceReport.json";
      const res = await fetchHelper.get({ url });
      setLocations(res.map((i) => ({ value: i.id, label: i.name })));
    } catch (err) {
      setLocations([{ value: user?.locations[0]?.id, label: user?.locations[0]?.name }]);
    }
  };
  const onChangeFilter = (data) => {
    setFilter({ ...data, temp: new Date().getTime() });
  };

  return (
    <div style={{ display: "flex", flex: 1 }}>
      <div style={{ display: "flex", width: 350 }}>
        {locations.length > 0 && user && user.locations.length && lIndex !== null ? (
          <Filter
            onChange={onChangeFilter}
            loading={loading}
            fields={[
              { type: "line" },
              {
                type: "dateRange",
                label: "Filter Implant Date",
                key: "dateRange",
                initialEnd: "12/30/2020",
                initialStart: "1/1/1990",
                optional: true,
                defaultEnabled: false,
              },
              {
                type: "multiSelect",
                operator: "$eq",
                key: "location",
                whereKey: "location.id",
                label: "Location",
                options: locations,
                initialSelected: [locations[lIndex]],
                maxSelect: 1,
                forceHasSelectAll: false,
              },
            ]}
          />
        ) : null}
      </div>
      <div style={{ display: "flex", flex: 1, padding: 10 }}>
        <Graph data={data} keys={keys} totalByKeys={totalByKeys} />
      </div>
    </div>
  );
};

export default AnalyticsDevice;
